import LoadingScreen from '@/components/LoadingScreen'
import { useEffect, useState } from 'react'
import { useAppSelector } from '@/hooks/useRedux'
import { userService } from '@/services/api'
import type { User } from '@/types'

interface FriendRequest {
  id: string
  from_user: User
  to_user: User
  status: 'pending' | 'accepted' | 'declined'
  created_at: string
}

export default function FriendsPage() {
  const { user } = useAppSelector((state) => state.auth)
  const [activeTab, setActiveTab] = useState<'all' | 'pending' | 'blocked'>('all')
  const [friends, setFriends] = useState<User[]>([])
  const [pendingRequests, setPendingRequests] = useState<FriendRequest[]>([])
  const [blockedUsers, setBlockedUsers] = useState<User[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<User[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadFriends()
  }, [])

  const loadFriends = async () => {
    setLoading(true)
    try {
      const [friendsResp, pendingResp, blockedResp] = await Promise.all([
        userService.getFriends(),
        userService.getFriendRequests(),
        userService.getBlockedUsers(),
      ])
      setFriends(friendsResp.data)
      setPendingRequests(pendingResp.data)
      setBlockedUsers(blockedResp.data)
    } catch (error) {
      console.error('Failed to load friends:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([])
      return
    }
    try {
      const response = await userService.search(searchQuery)
      setSearchResults(response.data)
    } catch (error) {
      console.error('Failed to search users:', error)
    }
  }

  const handleSendFriendRequest = async (targetUserId: string) => {
    try {
      await userService.sendFriendRequest(targetUserId)
      alert('Friend request sent!')
      setSearchResults([])
      setSearchQuery('')
    } catch (error) {
      console.error('Failed to send friend request:', error)
      alert('Failed to send friend request')
    }
  }

  const handleAcceptRequest = async (requestId: string) => {
    try {
      await userService.acceptFriendRequest(requestId)
      await loadFriends()
    } catch (error) {
      console.error('Failed to accept request:', error)
    }
  }

  const handleDeclineRequest = async (requestId: string) => {
    try {
      await userService.declineFriendRequest(requestId)
      setPendingRequests(pendingRequests.filter((r) => r.id !== requestId))
    } catch (error) {
      console.error('Failed to decline request:', error)
    }
  }

  const handleRemoveFriend = async (friendId: string) => {
    if (!confirm('Remove this friend?')) return
    try {
      await userService.removeFriend(friendId)
      setFriends(friends.filter((f) => f.id !== friendId))
    } catch (error) {
      console.error('Failed to remove friend:', error)
    }
  }

  const handleBlockUser = async (targetUserId: string) => {
    if (!confirm('Block this user?')) return
    try {
      await userService.blockUser(targetUserId)
      await loadFriends()
    } catch (error) {
      console.error('Failed to block user:', error)
    }
  }

  const handleUnblockUser = async (targetUserId: string) => {
    try {
      await userService.unblockUser(targetUserId)
      setBlockedUsers(blockedUsers.filter((u) => u.id !== targetUserId))
    } catch (error) {
      console.error('Failed to unblock user:', error)
    }
  }

  if (!user) {
    return <LoadingScreen />
  }

  return (
    <div className="min-h-screen bg-gray-900 p-8">
      <h1 className="text-4xl font-bold text-white mb-8">Friends</h1>

      {/* Search Bar */}
      <div className="mb-8">
        <div className="flex gap-4">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Search users by username or email..."
            className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded text-white placeholder-gray-400"
          />
          <button
            onClick={handleSearch}
            className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-6 rounded"
          >
            Search
          </button>
        </div>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="mt-4 bg-gray-800 rounded-lg p-4 space-y-2">
            {searchResults.map((result) => (
              <div
                key={result.id}
                className="flex items-center justify-between bg-gray-700 p-3 rounded"
              >
                <div className="flex items-center gap-3">
                  <img
                    src={result.avatar_url || '/default-avatar.png'}
                    alt={result.username}
                    className="w-10 h-10 rounded-full"
                  />
                  <div>
                    <p className="text-white font-semibold">{result.username}</p>
                    <p className="text-sm text-gray-400">{result.email}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleSendFriendRequest(result.id)}
                  className="bg-green-600 hover:bg-green-700 text-white font-bold py-1 px-4 rounded"
                >
                  Add Friend
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-8">
        {(['all', 'pending', 'blocked'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-6 py-2 rounded-lg font-semibold capitalize transition ${
              activeTab === tab
                ? 'bg-purple-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            {tab === 'all' ? `Friends (${friends.length})` : tab}
            {tab === 'pending' && pendingRequests.length > 0 && (
              <span className="ml-2 bg-red-600 px-2 py-1 rounded-full text-xs">
                {pendingRequests.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <LoadingScreen variant="section" />
      ) : (
        <div className="space-y-4">
          {activeTab === 'all' && (
            <>
              {friends.length === 0 ? (
                <p className="text-gray-400">No friends yet. Search for users to add!</p>
              ) : (
                friends.map((friend) => (
                  <div
                    key={friend.id}
                    className="flex items-center justify-between bg-gray-800 p-4 rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        <img
                          src={friend.avatar_url || '/default-avatar.png'}
                          alt={friend.username}
                          className="w-16 h-16 rounded-full"
                        />
                        <div
                          className={`absolute bottom-0 right-0 w-4 h-4 rounded-full border-2 border-gray-800 ${
                            friend.status === 'online' ? 'bg-green-500' : 'bg-gray-500'
                          }`}
                        />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">
                          {friend.display_name || friend.username}
                        </h3>
                        <p className="text-sm text-gray-400">@{friend.username}</p>
                        {friend.custom_status && (
                          <p className="text-sm text-gray-500">{friend.custom_status}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleBlockUser(friend.id)}
                        className="bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded"
                      >
                        Block
                      </button>
                      <button
                        onClick={() => handleRemoveFriend(friend.id)}
                        className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))
              )}
            </>
          )}

          {activeTab === 'pending' && (
            <>
              {pendingRequests.length === 0 ? (
                <p className="text-gray-400">No pending friend requests</p>
              ) : (
                pendingRequests.map((request) => (
                  <div
                    key={request.id}
                    className="flex items-center justify-between bg-gray-800 p-4 rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <img
                        src={request.from_user.avatar_url || '/default-avatar.png'}
                        alt={request.from_user.username}
                        className="w-16 h-16 rounded-full"
                      />
                      <div>
                        <h3 className="text-lg font-semibold text-white">
                          {request.from_user.username}
                        </h3>
                        <p className="text-sm text-gray-400">
                          Sent {new Date(request.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleAcceptRequest(request.id)}
                        className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
                      >
                        Accept
                      </button>
                      <button
                        onClick={() => handleDeclineRequest(request.id)}
                        className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
                      >
                        Decline
                      </button>
                    </div>
                  </div>
                ))
              )}
            </>
          )}

          {activeTab === 'blocked' && (
            <>
              {blockedUsers.length === 0 ? (
                <p className="text-gray-400">No blocked users</p>
              ) : (
                blockedUsers.map((blockedUser) => (
                  <div
                    key={blockedUser.id}
                    className="flex items-center justify-between bg-gray-800 p-4 rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <img
                        src={blockedUser.avatar_url || '/default-avatar.png'}
                        alt={blockedUser.username}
                        className="w-16 h-16 rounded-full"
                      />
                      <div>
                        <h3 className="text-lg font-semibold text-white">
                          {blockedUser.username}
                        </h3>
                        <p className="text-sm text-red-400">Blocked</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleUnblockUser(blockedUser.id)}
                      className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
                    >
                      Unblock
                    </button>
                  </div>
                ))
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
